import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { SOCIAL_FEED, CREATE_SOCIAL_POST, LIKE_SOCIAL_POST, COMMENT_ON_POST, DELETE_SOCIAL_POST } from '../graphql';
import { Heart, MessageCircle, ExternalLink, Plus, X, Send, Trash2, BookOpen, Youtube, Lightbulb, Code2, Globe, School } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const POST_TYPES = [
  { value: 'ARTICLE',  label: 'Article',      emoji: '📰', color: 'bg-blue-100 text-blue-700',   desc: 'Share a blog post or article' },
  { value: 'VIDEO',    label: 'Video',         emoji: '🎬', color: 'bg-red-100 text-red-700',     desc: 'Share a learning video/talk' },
  { value: 'PROJECT',  label: 'Project',       emoji: '🚀', color: 'bg-violet-100 text-violet-700', desc: 'Share what you\'re building' },
  { value: 'THOUGHT',  label: 'Thought',       emoji: '💡', color: 'bg-amber-100 text-amber-700', desc: 'Share a learning / insight' },
  { value: 'RESOURCE', label: 'Resource',      emoji: '📚', color: 'bg-green-100 text-green-700', desc: 'Useful tool, repo, or course' },
];

const TYPE_ICON = { ARTICLE: <BookOpen size={13}/>, VIDEO: <Youtube size={13}/>, PROJECT: <Code2 size={13}/>, THOUGHT: <Lightbulb size={13}/>, RESOURCE: <BookOpen size={13}/> };

function PostCard({ post, onLike, onComment, onDelete, currentUserId }) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isOwn = post.author?.id === currentUserId;
  const cfg = POST_TYPES.find(t => t.value === post.postType) || POST_TYPES[3];

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
  };

  return (
    <div className="card !p-5 hover:shadow-md transition-shadow group">
      {/* Author row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
            {post.author?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{post.author?.name}</p>
            <p className="text-xs text-gray-400">{post.author?.college} · {new Date(post.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
          {post.audience === 'COLLEGE'
            ? <span className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700"><School size={10}/> College</span>
            : <span className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full font-medium bg-gray-50 text-gray-400"><Globe size={10}/> Community</span>
          }
          {isOwn && <button onClick={() => onDelete(post.id)} className="opacity-0 group-hover:opacity-100 text-gray-200 hover:text-red-400 transition-all"><Trash2 size={13}/></button>}
        </div>
      </div>

      {/* Content */}
      <h3 className="font-bold text-gray-900 mb-1">{post.title}</h3>
      {post.body && <p className="text-sm text-gray-600 mb-2 leading-relaxed">{post.body}</p>}
      {post.url && (
        <a href={post.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline bg-indigo-50 px-3 py-1.5 rounded-xl mb-2">
          <ExternalLink size={13}/> {post.url.length > 50 ? post.url.slice(0, 50)+'…' : post.url}
        </a>
      )}

      {/* Tags */}
      {post.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {post.tags.map(t => <span key={t} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">#{t}</span>)}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 pt-3 border-t border-gray-50">
        <button onClick={() => onLike(post.id)} className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${post.hasLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}>
          <Heart size={15} fill={post.hasLiked ? 'currentColor' : 'none'}/> {post.likes}
        </button>
        <button onClick={() => setShowComments(s=>!s)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-indigo-500 transition-colors">
          <MessageCircle size={15}/> {post.comments?.length || 0}
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-3 space-y-2">
          {post.comments?.map(c => (
            <div key={c.id} className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-xs font-bold flex-shrink-0">{c.author?.name?.[0]}</div>
              <div className="bg-gray-50 rounded-xl px-3 py-1.5 flex-1">
                <span className="text-xs font-semibold text-gray-700">{c.author?.name} </span>
                <span className="text-xs text-gray-600">{c.body}</span>
              </div>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <input className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:border-indigo-400" value={commentText} onChange={e=>setCommentText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleComment()} placeholder="Add a comment…" />
            <button onClick={handleComment} disabled={submitting||!commentText.trim()} className="text-indigo-500 hover:text-indigo-700 disabled:opacity-30"><Send size={16}/></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TechSocialPage() {
  const { user } = useAuth();
  const [filterType, setFilterType] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [postType, setPostType] = useState('ARTICLE');
  const [audience, setAudience] = useState('COMMUNITY');
  const [form, setForm] = useState({ title:'', body:'', url:'', tagsInput:'' });
  const [postErr, setPostErr] = useState('');

  const { data, refetch } = useQuery(SOCIAL_FEED, { variables: { postType: filterType || undefined, tag: filterTag || undefined }, skip: !user });
  const [create, { loading: creating }] = useMutation(CREATE_SOCIAL_POST, {
    onCompleted: () => { refetch(); setShowCreate(false); setForm({ title:'',body:'',url:'',tagsInput:'' }); setAudience('COMMUNITY'); setPostErr(''); },
    onError: (e) => setPostErr(e.message?.includes('authenticated') ? 'Session expired — please log in again.' : e.message),
  });
  const [like] = useMutation(LIKE_SOCIAL_POST, { onCompleted: () => refetch() });
  const [comment] = useMutation(COMMENT_ON_POST, { onCompleted: () => refetch() });
  const [del] = useMutation(DELETE_SOCIAL_POST, { onCompleted: () => refetch() });

  const posts = data?.socialFeed || [];
  const cfg = POST_TYPES.find(t => t.value === postType) || POST_TYPES[0];

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setPostErr('Title is required'); return; }
    setPostErr('');
    await create({ variables: { postType, title: form.title, body: form.body || null, url: form.url || null, tags: form.tagsInput.split(',').map(s=>s.trim()).filter(Boolean), audience } });
  };

  const POPULAR_TAGS = ['campus-life','internship','career','dsa','web-dev','machine-learning','open-source','hackathon','placement-prep','resources','tutorial','project'];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Student Social</h1>
          <p className="text-gray-500 text-sm">Share what you're learning, building & living on campus · Grow together</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14}/> Share</button>
      </div>

      {/* Filters */}
      <div className="mb-4 space-y-2">
        {/* Type filter */}
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => { setFilterType(''); refetch(); }} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!filterType ? 'bg-gray-800 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>All</button>
          {POST_TYPES.map(t => (
            <button key={t.value} onClick={() => { const v = filterType === t.value ? '' : t.value; setFilterType(v); refetch({ postType: v || undefined }); }}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filterType===t.value ? 'bg-gray-800 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
              {t.label}
            </button>
          ))}
        </div>
        {/* Tag filter */}
        <div className="flex gap-1 flex-wrap">
          {POPULAR_TAGS.map(t => (
            <button key={t} onClick={() => { const v = filterTag === t ? '' : t; setFilterTag(v); refetch({ tag: v || undefined }); }}
              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${filterTag===t ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-100 text-gray-400 hover:border-indigo-300'}`}>
              #{t}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm text-gray-400 mb-4">
        <span>{posts.length} posts</span>
        <span>·</span>
        <span>{posts.reduce((s,p) => s + p.likes, 0)} likes</span>
        <span>·</span>
        <span>{posts.reduce((s,p) => s + (p.comments?.length||0), 0)} comments</span>
      </div>

      {/* Feed */}
      {posts.length === 0
        ? <div className="text-center py-16"><p className="text-gray-500">No posts yet — share something cool!</p><button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">+ Share First Post</button></div>
        : <div className="space-y-4">
            {posts.map(p => (
              <PostCard key={p.id} post={p} currentUserId={user?.id}
                onLike={id => like({ variables: { postId: id } })}
                onComment={(id, body) => comment({ variables: { postId: id, body } })}
                onDelete={id => del({ variables: { postId: id } })} />
            ))}
          </div>
      }

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Share something</h2>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              {/* Post type selector */}
              <div className="grid grid-cols-5 gap-1">
                {POST_TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => setPostType(t.value)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-colors text-xs font-medium ${postType===t.value ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}>
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 -mt-2">{cfg.desc}</p>

              <input required className="input w-full" placeholder="Title / headline" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} />

              {postType !== 'THOUGHT' && (
                <input className="input w-full" type="url" placeholder={postType === 'VIDEO' ? 'YouTube / video URL' : postType === 'ARTICLE' ? 'Article URL' : 'Link (optional)'} value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} />
              )}

              <textarea className="input w-full resize-none h-24 text-sm" placeholder={postType === 'THOUGHT' ? 'Share your insight, question, or lesson learned…' : 'Description / summary (optional)'} value={form.body} onChange={e=>setForm(f=>({...f,body:e.target.value}))} />

              <input className="input w-full" placeholder="Tags (comma-sep: javascript, dsa, tutorial)" value={form.tagsInput} onChange={e=>setForm(f=>({...f,tagsInput:e.target.value}))} />
              <div className="flex flex-wrap gap-1">
                {POPULAR_TAGS.map(t => <button key={t} type="button" onClick={() => { const cur = form.tagsInput ? form.tagsInput.split(',').map(s=>s.trim()).filter(Boolean) : []; if (!cur.includes(t)) setForm(f=>({...f,tagsInput:[...cur,t].join(', ')})); }} className="text-xs px-2 py-0.5 rounded-full border border-gray-200 text-gray-400 hover:border-indigo-300 hover:text-indigo-500">+{t}</button>)}
              </div>

              {/* Audience picker */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Visible to</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setAudience('COMMUNITY')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-medium transition-colors flex-1 justify-center ${audience === 'COMMUNITY' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}>
                    <Globe size={14}/> Everyone (Aura Community)
                  </button>
                  <button type="button" onClick={() => setAudience('COLLEGE')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-medium transition-colors flex-1 justify-center ${audience === 'COLLEGE' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}>
                    <School size={14}/> My College
                  </button>
                </div>
              </div>

              {postErr && <p className="text-sm text-red-500">{postErr}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Sharing…' : 'Share'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
